import { getCustomCritiquePrompt, setCustomCritiquePrompt } from "../../core/storage";
import {
  getStrings,
  wireLanguageToggleButtons,
  updateLanguageToggleButtons,
} from "../../core/i18n";
import { escapeHtml, labelWithInfo, wireInfoIcons } from "./domUtils";
import { CRITIQUE_CHAPTER_PROMPT, MINIMUM_CRITIQUE_PROMPT } from "./chatPrompts";

// App-wide settings — reached via the gear icon in the chat screen's nav-cluster row (see
// sessionSwitcher.ts), a real top-level screen (taskpane.ts's Screen router) rather than
// something nested in chat, same reasoning as Story Bible's own list screen: settings apply
// across every chat, not to the one currently open. Two independent sections
// (`.settings-section`, visually divided so Save/Revert/Cancel below the Critique prompt field
// clearly don't apply to the Language row too — an earlier version had them read as one form):
// the Critique prompt override, and a second instance of the EN/CH/JP language toggle (the
// footer's copy still exists too — see i18n.ts's own comment on multiple toggle instances being
// expected, not a bug). Deliberately not `languageToggleHtml()`'s shared "EN / CH / JP" markup —
// that one is centered and abbreviated, tuned for sitting alone on a sparse onboarding-style
// screen; this page reads more like a form, so it gets its own left-aligned
// "Language: English / Chinese / Japanese" row instead, built directly against the same
// `data-lang`/wireLanguageToggleButtons mechanism.

export interface SettingsHandlers {
  onBackToChat: () => void;
}

export function renderSettings(container: HTMLElement, handlers: SettingsHandlers): void {
  // Pre-filled with the author's own saved override if they've ever customized it, otherwise
  // the app's built-in rubric — same resolution chat.ts's sendCritique uses via
  // effectiveCritiquePrompt(), so what's shown here always matches what "Critique this" would
  // actually send right now. Captured once, not re-derived on every render() call, same "local
  // unsaved draft state" model storyBible.ts's editor uses.
  let critiquePrompt = getCustomCritiquePrompt() ?? CRITIQUE_CHAPTER_PROMPT;

  function render(): void {
    // Read fresh on every call, not hoisted above render() — this function re-runs after a
    // language switch (see wireLanguageToggleButtons below), and a stale outer copy of `s` was
    // exactly why the title/labels used to only update after leaving and re-entering this screen.
    const s = getStrings();
    container.innerHTML = `
      <div class="screen settings-screen">
        <div class="screen-header">
          <h2>${s.settings.title}</h2>
        </div>
        <div class="settings-section">
          <label id="settings-critique-prompt-label">${labelWithInfo(s.settings.critiquePromptLabel, s.settings.critiquePromptInfo)}
            <textarea id="settings-critique-prompt" rows="12">${escapeHtml(critiquePrompt)}</textarea>
          </label>
          <div class="form-actions">
            <button type="button" class="primary-btn" id="settings-save-btn">${s.common.save}</button>
            <button type="button" class="secondary-btn" id="settings-revert-btn">${s.settings.revertToDefault}</button>
            <button type="button" class="link-btn" id="settings-cancel-btn">${s.common.cancel}</button>
          </div>
        </div>
        <div class="settings-section settings-language-row">
          <span>${s.settings.languageLabel}:</span>
          <button type="button" class="settings-lang-btn" data-lang="en">${s.settings.languageEnglish}</button>
          <span class="settings-language-sep">/</span>
          <button type="button" class="settings-lang-btn" data-lang="zh">${s.settings.languageChinese}</button>
          <span class="settings-language-sep">/</span>
          <button type="button" class="settings-lang-btn" data-lang="ja">${s.settings.languageJapanese}</button>
        </div>
      </div>
    `;

    wireInfoIcons(container);

    const textarea = container.querySelector("#settings-critique-prompt") as HTMLTextAreaElement;
    textarea.addEventListener("input", () => {
      critiquePrompt = textarea.value;
    });

    container.querySelector("#settings-revert-btn")?.addEventListener("click", () => {
      critiquePrompt = CRITIQUE_CHAPTER_PROMPT;
      render();
    });

    container.querySelector("#settings-save-btn")?.addEventListener("click", () => {
      const trimmed = critiquePrompt.trim();
      // A blank save doesn't persist an actually-empty prompt — "Critique this" always needs
      // something to send, so an empty field falls back to a short built-in minimum instead of
      // silently breaking that button (see chatPrompts.ts's MINIMUM_CRITIQUE_PROMPT).
      setCustomCritiquePrompt(trimmed || MINIMUM_CRITIQUE_PROMPT);
      handlers.onBackToChat();
    });

    container
      .querySelector("#settings-cancel-btn")
      ?.addEventListener("click", handlers.onBackToChat);

    wireLanguageToggleButtons(container, render);
    updateLanguageToggleButtons();
  }

  render();
}
